import { useState } from "react";
import { useTranslation } from "react-i18next";
import { format, addDays, parseISO } from "date-fns";
import { de, enGB, es, ru, uk } from "date-fns/locale";
import type { Locale } from "date-fns";
import i18n from "@/i18n";
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ChevronRight,
  Coffee,
  Layers,
  Sparkles,
} from "lucide-react";
import { BackButton } from "@/components/ui/BackButton";
import { getWeek } from "@/api/plan";
import { triggerWeeklyReview } from "@/api/coach";
import { Card, SectionLabel } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { fmtWeekRange } from "@/lib/dates";
import { fmtDuration, SPORT_LABELS } from "@/lib/tss";
import type { PlannedWorkout, SessionBlock } from "@/types";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const _DATE_LOCALES_WD: Record<string, Locale> = { en: enGB, ru, uk, es, de };
function _weekdayLabel(idx: number): string {
  const lng = (i18n.language || "en").slice(0, 2).toLowerCase();
  const loc = _DATE_LOCALES_WD[lng] || enGB;
  // Mon=0..Sun=6, anchor on a known Monday (Jan 5 1970)
  const d = new Date(1970, 0, 5 + idx);
  return format(d, "EEEEEE", { locale: loc });
}

const KEY_SESSION_TYPES = new Set([
  "sub_threshold",
  "threshold",
  "sweet_spot",
  "brick",
  "brick_race_pace",
  "race_pace",
  "race_simulation",
  "race_rehearsal",
  "long",
  "vo2",
  "long_z2",
]);

function isKeySession(w: PlannedWorkout): boolean {
  if ((w.session_blocks || []).length > 0) return true;
  if (KEY_SESSION_TYPES.has(w.type)) return true;
  if (w.duration_minutes >= 90) return true;
  return false;
}

function workoutsByDayOffset(
  workouts: PlannedWorkout[],
  weekStartISO: string,
): Map<number, PlannedWorkout[]> {
  const start = parseISO(weekStartISO);
  const startMs = new Date(
    start.getFullYear(),
    start.getMonth(),
    start.getDate(),
  ).getTime();
  const m = new Map<number, PlannedWorkout[]>();
  for (const w of workouts) {
    const d = parseISO(w.date);
    const dMs = new Date(
      d.getFullYear(),
      d.getMonth(),
      d.getDate(),
    ).getTime();
    const offset = Math.round((dMs - startMs) / 86400000);
    if (offset < 0 || offset > 6) continue;
    const arr = m.get(offset);
    if (arr) arr.push(w);
    else m.set(offset, [w]);
  }
  return m;
}

// ---------------------------------------------------------------------------
// Day cards
// ---------------------------------------------------------------------------

function DayHeader({ date, offset }: { date: Date; offset: number }) {
  return (
    <div className="flex w-16 shrink-0 flex-col items-center">
      <span className="text-[10px] uppercase tracking-wider text-taupe">
        {_weekdayLabel(offset)}
      </span>
      <span className="metric-number text-[18px]">{date.getDate()}</span>
      <span className="text-[10px] text-taupe">
        {format(date, "MMM", { locale: ru })}
      </span>
    </div>
  );
}

function RestDayCard({ date, offset }: { date: Date; offset: number }) {
  const { t } = useTranslation();
  const isSun = offset === 6;
  return (
    <Card className="flex items-center gap-4 bg-cream/60">
      <DayHeader date={date} offset={offset} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <Coffee className="h-3.5 w-3.5 text-amber-dark" strokeWidth={1.8} />
          <span className="text-[10px] font-medium uppercase tracking-wider text-amber-dark">
            Rest day
          </span>
        </div>
        <div className="mt-1 font-serif text-[16px] text-navy-deep">
          {isSun ? t("week_detail.rest_full_sun") : t("week_detail.rest_day")}
        </div>
        <div className="mt-1 text-[12px] text-taupe">
          {t("week_detail.rest_desc")}
        </div>
      </div>
    </Card>
  );
}

function SimpleWorkoutCard({
  workout,
  date,
  offset,
  week,
}: {
  workout: PlannedWorkout;
  date: Date;
  offset: number;
  week: number;
}) {
  return (
    <Link
      to={`/workout/planned/${workout.id}`}
      state={{ week }}
    >
      <Card
        className={
          isKeySession(workout)
            ? "flex items-center gap-4 border-amber/40 ring-1 ring-amber/20 hover:border-amber/60"
            : "flex items-center gap-4 hover:border-amber/40"
        }
      >
        <DayHeader date={date} offset={offset} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="rounded-full bg-amber/15 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-amber-dark">
              {SPORT_LABELS[workout.sport] ?? workout.sport}
            </span>
            <span className="text-[12px] text-taupe">{workout.type}</span>
            {isKeySession(workout) ? (
              <span className="rounded-full bg-navy px-2 py-0.5 text-[9px] font-medium uppercase tracking-wider text-amber-light">
                key
              </span>
            ) : null}
          </div>
          <div className="mt-0.5 font-medium leading-tight">{workout.title}</div>
          <div className="mt-0.5 text-[12px] text-taupe">
            {fmtDuration(workout.duration_minutes)} · TSS {workout.target_tss}
          </div>
        </div>
        <ChevronRight className="h-5 w-5 text-taupe-light" strokeWidth={1.6} />
      </Card>
    </Link>
  );
}

function CompoundWorkoutCard({
  workout,
  date,
  offset,
  week,
}: {
  workout: PlannedWorkout;
  date: Date;
  offset: number;
  week: number;
}) {
  const blocks = workout.session_blocks;
  return (
    <Link
      to={`/workout/planned/${workout.id}`}
      state={{ week }}
    >
      <Card className="border-amber/40 ring-1 ring-amber/20 hover:border-amber/60">
        <div className="flex items-start gap-4">
          <DayHeader date={date} offset={offset} />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1 rounded-full bg-amber/15 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-amber-dark">
                <Layers className="h-3 w-3" strokeWidth={1.8} /> Compound
              </span>
              <span className="text-[12px] text-taupe">
                {blocks.map((b) => SPORT_LABELS[b.sport] ?? b.sport).join(" + ")}
              </span>
            </div>
            <div className="mt-1 font-medium leading-tight">{workout.title}</div>

            <div className="mt-3 space-y-2 border-l-2 border-amber/30 pl-3">
              {blocks.map((b, i) => (
                <SessionBlockLine key={i} block={b} />
              ))}
            </div>

            <div className="mt-3 border-t border-cream-border pt-2 text-[12px] text-taupe">
              {fmtDuration(workout.duration_minutes)} · TSS {workout.target_tss}{" "}
              total
            </div>
          </div>
          <ChevronRight className="h-5 w-5 text-taupe-light" strokeWidth={1.6} />
        </div>
      </Card>
    </Link>
  );
}

function SessionBlockLine({ block }: { block: SessionBlock }) {
  const label =
    block.block === "endurance"
      ? `${SPORT_LABELS[block.sport] ?? block.sport} · ${block.target || "—"}`
      : block.block === "strength"
        ? `Strength · ${block.focus || "—"}${block.back_safe ? " · back-safe" : ""}`
        : `${block.block} · ${block.target || ""}`;
  return (
    <div>
      <div className="text-[13px] font-medium text-navy-deep">{label}</div>
      <div className="text-[11px] text-taupe">
        {fmtDuration(block.duration_min)}
        {block.tss != null ? ` · TSS ${block.tss}` : ""}
      </div>
      {block.exercises && block.exercises.length > 0 ? (
        <ul className="mt-1 space-y-0.5 text-[11px] text-taupe">
          {block.exercises.map((ex, i) => (
            <li key={i}>· {ex}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function WeekDetail() {
  const { t } = useTranslation();
  const { weekNumber } = useParams<{ weekNumber: string }>();
  const n = Number(weekNumber);
  const qc = useQueryClient();
  const [regenMsg, setRegenMsg] = useState<string | null>(null);

  const { data: week, isLoading } = useQuery({
    queryKey: ["week", n],
    queryFn: () => getWeek(n),
    enabled: !Number.isNaN(n),
  });

  const regen = useMutation({
    mutationFn: triggerWeeklyReview,
    onSuccess: (r) => {
      if (r.status === "skipped") {
        setRegenMsg(t("common.generation_skipped"));
        return;
      }
      setRegenMsg(r.status === "cached"
        ? t("common.generation_cached")
        : t("week_detail.regen_success", { n: r.workouts_created, model: r.model_used }));
      qc.invalidateQueries({ queryKey: ["week", n] });
      qc.invalidateQueries({ queryKey: ["plan"] });
      qc.invalidateQueries({ queryKey: ["plan-workouts"] });
    },
    onError: () =>
      setRegenMsg(t("week_detail.regen_fail")),
  });

  if (isLoading) {
    return <div className="py-10 text-center text-taupe">{t("week_detail.loading")}</div>;
  }
  if (!week) {
    return <div className="py-10 text-center text-coral">{t("week_detail.week_not_found")}</div>;
  }

  const byOffset = workoutsByDayOffset(week.workouts, week.week_start_date);
  const trainingDaysCount = [...byOffset.keys()].length;
  const restDaysCount = 7 - trainingDaysCount;
  const keySessionsCount = week.workouts.filter(isKeySession).length;

  return (
    <div className="space-y-5">
      <BackButton to="/plan" label={t("week_detail.back_to_plan")} />

      <div>
        <SectionLabel>
          {t("week_detail.week_phase", { n: week.week_number, phase: week.phase })}
        </SectionLabel>
        <h1 className="mt-1 font-serif text-[28px] font-medium tracking-tight">
          {fmtWeekRange(week.week_start_date)}
        </h1>
      </div>

      {/* Week summary metric strip */}
      <Card className="px-4 py-3">
        <div className="grid grid-cols-4 gap-3 text-center">
          <SummaryMetric label="Training days" value={trainingDaysCount} />
          <SummaryMetric label="Rest days" value={restDaysCount} />
          <SummaryMetric label="Key sessions" value={keySessionsCount} />
          <SummaryMetric label="Target TSS" value={week.target_tss} />
        </div>
      </Card>

      {week.claude_review ? (
        <Card tone="navy">
          <SectionLabel className="text-amber-light">
            {t("week_detail.coach_review")}
          </SectionLabel>
          <p className="coach-voice mt-3 whitespace-pre-wrap">
            {week.claude_review}
          </p>
          {week.adjustments_rationale ? (
            <p className="mt-3 border-t border-navy-soft/50 pt-3 text-[13px] italic text-cream-border/80">
              {week.adjustments_rationale}
            </p>
          ) : null}
        </Card>
      ) : (
        <Card tone="navy">
          <SectionLabel className="text-amber-light">
            {t("week_detail.plan_not_built")}
          </SectionLabel>
          <p className="coach-voice mt-3">
            {t("week_detail.plan_not_built_desc")}
          </p>
        </Card>
      )}

      <Button
        variant="secondary"
        size="md"
        className="w-full"
        loading={regen.isPending}
        onClick={() => regen.mutate()}
      >
        <Sparkles className="h-4 w-4" />
        {week.workouts.length === 0
          ? t("week_detail.generate_workouts")
          : t("week_detail.regenerate_week")}
      </Button>
      {regenMsg ? (
        <div className="rounded-lg bg-amber/15 px-3 py-2 text-[13px] text-amber-dark">
          {regenMsg}
        </div>
      ) : null}

      {/* 7-day grid: either workout(s) on that day, or Rest card */}
      {week.workouts.length > 0 ? (
        <div className="space-y-2.5">
          <SectionLabel>{t("week_detail.weekly_workouts")}</SectionLabel>
          {Array.from({ length: 7 }).map((_, offset) => {
            const date = addDays(parseISO(week.week_start_date), offset);
            const dayWorkouts = byOffset.get(offset) || [];
            if (dayWorkouts.length === 0) {
              return (
                <RestDayCard
                  key={`rest-${offset}`}
                  date={date}
                  offset={offset}
                />
              );
            }
            return (
              <div key={`day-${offset}`} className="space-y-2">
                {dayWorkouts.map((w) =>
                  (w.session_blocks || []).length > 0 ? (
                    <CompoundWorkoutCard
                      key={w.id}
                      workout={w}
                      date={date}
                      offset={offset}
                      week={week.week_number}
                    />
                  ) : (
                    <SimpleWorkoutCard
                      key={w.id}
                      workout={w}
                      date={date}
                      offset={offset}
                      week={week.week_number}
                    />
                  ),
                )}
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function SummaryMetric({
  label,
  value,
}: {
  label: string;
  value: number | string;
}) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-taupe">
        {label}
      </div>
      <div className="metric-number mt-0.5 text-[22px]">{value}</div>
    </div>
  );
}
